import React, { useEffect, useState } from 'react';
import { loadClinicLogos } from '../utils/clinicLogo';
import { themeByName } from '../utils/clinicTheme';
import { useClinicTheme } from '../components/ThemeProvider';
import DoctorDashboard from './DoctorDashboard';
import ReceptionDashboard from './ReceptionDashboard';
import TherapistDashboard from './TherapistDashboard';
import PharmacyDashboard from './PharmacyDashboard';
import FinanceDashboard from './FinanceDashboard';
import HrDashboard from './HrDashboard';
import AdminDashboard from './AdminDashboard';

const UnifiedDashboard = () => {
  const [activeTab, setActiveTab] = useState('doctor');
  const [headerLogo, setHeaderLogo] = useState(null);
  const appearance = useClinicTheme();
  const clinicTheme = themeByName(appearance.theme);

  useEffect(() => {
    const apply = (logo) => setHeaderLogo(logo || null);
    loadClinicLogos().then((logos) => apply(logos.headerLogo));
    const onLogo = (event) => apply(event.detail);
    window.addEventListener('clinic-header-logo', onLogo);
    return () => window.removeEventListener('clinic-header-logo', onLogo);
  }, []);

  const getStoredUser = () => {
    const user = localStorage.getItem('user');
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch {
      return null;
    }
  };

  const storedUser = getStoredUser();
  const roleNames = new Set();
  if (storedUser?.role) roleNames.add(storedUser.role);
  (storedUser?.roles || []).forEach((assignedRole) => {
    if (assignedRole?.ROLE_NAME) roleNames.add(assignedRole.ROLE_NAME);
    if (assignedRole?.DASHBOARD_NAME) roleNames.add(assignedRole.DASHBOARD_NAME);
  });
  (storedUser?.dashboards || []).forEach((dashboardName) => roleNames.add(dashboardName));

  const isAdmin = roleNames.has('Admin');

  const getAvailableTabs = () => {
    const tabs = [];
    const allow = (name) => isAdmin || roleNames.has(name);

    if (allow('Admin')) {
      tabs.push({ id: 'admin', label: '⚙️ Admin Dashboard', color: '#dc3545' });
    }
    if (allow('Doctor')) {
      tabs.push({ id: 'doctor', label: '👨‍⚕️ Doctor Dashboard', color: '#007bff' });
    }
    if (allow('Receptionist')) {
      tabs.push({ id: 'reception', label: '🏥 Reception Dashboard', color: '#28a745' });
    }
    if (allow('Therapist')) {
      tabs.push({ id: 'therapist', label: '🏥 Therapist Dashboard', color: '#ffc107' });
    }
    if (allow('Pharmacy')) {
      tabs.push({ id: 'pharmacy', label: '💊 Pharmacy Dashboard', color: '#6f42c1' });
    }
    if (allow('Finance')) {
      tabs.push({ id: 'finance', label: '💰 Finance Dashboard', color: '#198754' });
    }
    if (allow('HR')) {
      tabs.push({ id: 'hr', label: '👥 HR Dashboard', color: '#0f766e' });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();
  const primaryRole = isAdmin
    ? 'Admin'
    : (storedUser?.role || Array.from(roleNames)[0] || null);

  React.useEffect(() => {
    if (availableTabs.length === 0) return;
    const preferredId = {
      Admin: 'admin',
      Doctor: 'doctor',
      Receptionist: 'reception',
      Therapist: 'therapist',
      Pharmacy: 'pharmacy',
      Finance: 'finance',
      HR: 'hr'
    }[primaryRole];
    const defaultTab = availableTabs.find((tab) => tab.id === preferredId) || availableTabs[0];
    setActiveTab(defaultTab.id);
  }, [primaryRole, availableTabs.length]);

  const renderActiveDashboard = () => {
    switch (activeTab) {
      case 'admin':
        return <AdminDashboard />;
      case 'doctor':
        return <DoctorDashboard />;
      case 'reception':
        return <ReceptionDashboard />;
      case 'therapist':
        return <TherapistDashboard />;
      case 'pharmacy':
        return <PharmacyDashboard />;
      case 'finance':
        return <FinanceDashboard />;
      case 'hr':
        return <HrDashboard />;
      default:
        return <div>No dashboard available</div>;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--theme-wash, #f8f9fa)' }}>
      {/* Tab Navigation */}
      <div style={{ 
        backgroundColor: '#fff', 
        borderBottom: '2px solid #e9ecef',
        padding: '0 2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          gap: '0',
          maxWidth: '1200px',
          margin: '0 auto',
          alignItems: 'center'
        }}>
          {headerLogo && (
            <img
              src={headerLogo}
              alt="Clinic logo"
              style={{ height: 44, width: 'auto', maxWidth: 160, objectFit: 'contain', marginRight: '1rem' }}
            />
          )}
          {availableTabs.map((tab) => {
            const color = clinicTheme.keepRoleColors ? tab.color : clinicTheme.primary;
            return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '1rem 2rem',
                border: 'none',
                backgroundColor: activeTab === tab.id ? color : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#6c757d',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                borderBottom: activeTab === tab.id ? `3px solid ${color}` : '3px solid transparent',
                transition: 'all 0.3s ease',
                borderRadius: '8px 8px 0 0',
                marginRight: '0.5rem',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.backgroundColor = `${color}20`;
                  e.target.style.color = color;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6c757d';
                }
              }}
            >
              {tab.label}
            </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/';
            }}
            style={{
              marginLeft: 'auto',
              padding: '0.4rem 0.8rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Dashboard Content */}
      <div style={{ 
        padding: '2rem',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {renderActiveDashboard()}
      </div>
    </div>
  );
};

export default UnifiedDashboard;
